import React, { useCallback, useEffect, useRef, useState } from 'react';

import useIsGrowthbookReady from '@editor/hooks/useIsGrowthbookReady';
import Masonry from '@repo/editor/src/components/Masonry/Masonry';
import { ColumnItem } from '@repo/editor/src/components/Masonry/types';
import { withEventScope } from '@repo/event-processor';
import { Button, secondaryTheme } from '@repo/ui/components/Buttons';
import { Spacer } from '@repo/ui/components/Spacer';

import NoPostsLoaded from 'components/NoPostsLoaded/NoPostsLoaded';
import PostPreview from 'components/PostPreview/PostPreview';
import { useRelatedTemplatesSemanticEnabled } from 'global/growthbookUtils';
import postApi from 'global/postApi';
import { DesignPostData } from 'global/types/DesignPostData';
import { postsStoreSelector, usePostsStore } from 'stores/postsStore';
import { FlexRow } from 'styles/layouts';

import { Title } from './styles';
import { RelatedDesignsProps } from './types';

const RELATED_DESIGNS_TAKE = 12;

/**
 * load and display related designs
 */
const RelatedDesigns = ({
  postId,
  designs,
  largeTitle,
  onClickDesign,
}: RelatedDesignsProps) => {
  const getPost = usePostsStore(postsStoreSelector.getPost);
  const addPost = usePostsStore(postsStoreSelector.addPost);
  const [posts, setPosts] = useState(designs || []);
  const fetchState = useRef({
    skip: designs?.length || 0,
    take: RELATED_DESIGNS_TAKE,
  });
  const [loading, setLoading] = useState(false);
  const [finished, setFinished] = useState(false);

  const unmounted = useRef(false);
  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
    };
  }, []);

  // TODO: Remove with https://app.clickup.com/t/86c39t0gr
  const isRelatedTemplatesSemanticEnabled =
    useRelatedTemplatesSemanticEnabled();

  const fetch = useCallback(async () => {
    const { skip, take } = fetchState.current;

    if (loading || finished) {
      return;
    }
    setLoading(true);

    const result = await postApi.getRelated(
      postId,
      take,
      skip,
      isRelatedTemplatesSemanticEnabled
    );
    if (unmounted.current) {
      return;
    }
    if (!result?.results?.length) {
      setFinished(true);
      setLoading(false);
      return;
    }

    result.results.forEach((post) => {
      if (!getPost(post.id)) {
        addPost(post);
      }
    });
    setPosts((posts) => [
      ...posts,
      ...result.results.filter(
        (post) => !posts.some((oldPost) => oldPost.id === post.id)
      ),
    ]);

    if (result.results.length < take) {
      setFinished(true);
    }

    fetchState.current.skip += take;
    setLoading(false);
  }, [
    finished,
    loading,
    postId,
    addPost,
    getPost,
    isRelatedTemplatesSemanticEnabled,
  ]);

  const isGrowthbookReady = useIsGrowthbookReady();

  useEffect(() => {
    if (posts.length || designs?.length || !isGrowthbookReady) {
      return;
    }
    fetch();
  }, [fetch, designs, posts, isGrowthbookReady]);

  useEffect(() => {
    setPosts([]);
    fetchState.current.skip = 0;
    setFinished(false);
  }, [postId]);

  const Preview = (post: ColumnItem<DesignPostData>) => (
    <PostPreview canLike post={post} onClick={onClickDesign} />
  );

  return (
    <>
      <Title large={largeTitle}>Related Designs</Title>
      <Masonry gap={24} items={posts} minColumnWidth={249} render={Preview} />
      {!posts.length && <NoPostsLoaded loading={loading} />}
      {!finished && !!posts.length && (
        <>
          <Spacer height="24px" />
          <FlexRow justifyContent="center">
            <Button
              height="40px"
              label="Show more"
              loading={loading}
              name="showMoreRelatedDesignsButton"
              theme={secondaryTheme}
              width="auto"
              onClick={fetch}
            />
          </FlexRow>
        </>
      )}
    </>
  );
};

export default withEventScope(RelatedDesigns, { feature: 'relatedDesigns' });
